import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft as ArrowLeftIcon,
  ShieldCheck as ShieldCheckIcon,
  AlertTriangle as ExclamationTriangleIcon,
  Loader2 as LoaderIcon,
  Search,
  ChevronDown,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'
const FrameworkDetails = () => {
  const { scanId, frameworkId } = useParams();
  const navigate = useNavigate();
  const [frameworkData, setFrameworkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedControls, setExpandedControls] = useState({});

  useEffect(() => {
    fetchFrameworkData();
  }, [scanId, frameworkId]);

  const fetchFrameworkData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(ApiNames.REPORTS_DETAILED(scanId, frameworkId));


      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Framework data not found for this scan ID');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFrameworkData(data);
    } catch (error) {
      console.error('Error fetching framework data:', error);
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate(`/dashboard/${scanId}`);
  };

  const toggleControlExpansion = (controlId) => {
    setExpandedControls(prev => ({
      ...prev,
      [controlId]: !prev[controlId]
    }));
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pass':
      case 'compliant':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'fail':
      case 'non_compliant':
      case 'non compliant':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'partial':
      case 'partially_compliant':
      case 'partially compliant':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      default:
        return <Clock className="w-5 h-5 text-cyan-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'pass':
      case 'compliant':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Compliant</span>;
      case 'fail':
      case 'non_compliant':
      case 'non compliant':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Non Compliant</span>;
      case 'partial':
      case 'partially_compliant':
      case 'partially compliant':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Partially Compliant</span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">Not Evaluated</span>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Critical</span>;
      case 'high':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">High</span>;
      case 'medium':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Medium</span>;
      case 'low':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Low</span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Medium</span>;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressBarColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[--ds-gray-50-bg] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <LoaderIcon className="w-8 h-8 text-primary-600 animate-spin" />
          </div>
          <h2 className="text-xl font-medium text-[--ds-text-900] mb-2">Loading Framework Details</h2>
          <p className="text-[--ds-text-600]">Fetching detailed control information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[--ds-gray-50-bg] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-medium text-[--ds-text-900] mb-2">Framework Error</h2>
          <p className="text-[--ds-text-600] mb-6">{error}</p>
          <button
            onClick={handleBackToDashboard}
            className="btn-primary"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const framework = frameworkData?.framework_data;
  // Handle different data structures for different frameworks
  const controls = framework?.control_evaluations || framework?.controls || [];
  const filteredControls = controls.filter(control =>
    control.controlName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    control.controlId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    control.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getFrameworkTitle = (fwId) => {
    switch (fwId) {
      case 'USWDS':
        return 'U.S. Web Design System';
      case 'OMB-M-15-13':
        return 'OMB M-15-13 HTTPS Everywhere';
      default:
        return fwId;
    }
  };

  const getFrameworkSubtitle = (fwId) => {
    switch (fwId) {
      case 'USWDS':
        return 'Design System Standards for Federal Websites';
      case 'OMB-M-15-13':
        return 'HTTPS Security Standards for Federal Information Systems';
      default:
        return 'Federal Compliance Framework';
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-[--main-ds-header] shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] md:border-b md:border-gray-200">
        <div className="">
          <div className="flex justify-between items-center md:py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackToDashboard}
                className="absolute top-[6rem] right-[24px] md:top-[0] md:right-[0] md:relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Framework Header */}
      <div className="md:mt-4 md:mb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-[--main-ds-card-grad] shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] rounded-md border-b border-gray-200">
          <div className="flex items-center space-x-4 mb-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${frameworkId === 'USWDS' ? 'bg-green-100' :
              frameworkId === 'OMB-M-15-13' ? 'bg-blue-100' : 'bg-gray-100'
              }`}>
              <ShieldCheckIcon className={`w-6 h-6 ${frameworkId === 'USWDS' ? 'text-green-600' :
                frameworkId === 'OMB-M-15-13' ? 'text-blue-600' : 'text-gray-600'
                }`} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[--ds-text-900] mb-0">{getFrameworkTitle(frameworkId)}</h1>
              <p className="text-[--ds-text-600] mb-0">{getFrameworkSubtitle(frameworkId)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-5">
            <div className="border border-primary bg-blue-100 p-4 rounded-md">
              <p className="text-sm text-black font-semibold">Category</p>
              <p className="text-md font-semibold text-blue-600 mb-0">
                {frameworkId === 'USWDS' ? 'Design System' : 'Security Framework'}
              </p>
            </div>
            <div className="border border-warning bg-warning-100 p-4 rounded-md">
              <p className="text-sm text-black font-semibold">Controls Scanned</p>
              <p className="text-md font-semibold text-warning-500 mb-0">
                {controls.length} of {controls.length}
              </p>
            </div>
            <div className="border border-success bg-success-100 p-4 rounded-md">
              <p className="text-sm text-black font-semibold">Pass Rate</p>
              <p className="text-md font-semibold text-success-600 mb-0">
                {framework?.summary?.overall_score || framework?.summary?.overallScore || 0}%
              </p>
            </div>
            <div className="border-red bg-red-100 p-4 rounded-md">
              <p className="text-sm text-black font-semibold">Avg Score</p>
              <p className={`text-md font-semibold mb-0 ${getScoreColor(framework?.summary?.overall_score || framework?.summary?.overallScore || 0)}`}>
                {Math.round(framework?.summary?.overall_score || framework?.summary?.overallScore || 0)}/100
              </p>
            </div>
            <div className="border-red bg-red-100 p-4 rounded-md">
              <p className="text-sm text-black font-semibold">Critical Issues</p>
              <p className="text-md font-semibold text-red-600 mb-0">
                {controls.filter(c => c.priority === 'Critical' || c.priority === 'High').length}
              </p>
            </div>
          </div>

          {/* Status Overview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="text-center">
              <p className="inline-flex justify-center items-center w-12 h-12 rounded-full text-2xl font-bold text-green-600 bg-green-100">
                {framework?.summary?.compliant || controls.filter(c => c.status === 'PASS' || c.status === 'Compliant').length}
              </p>
              <p className="text-sm text-[--ds-text-500] font-medium">Compliant</p>
            </div>
            <div className="text-center">
              <p className="inline-flex justify-center items-center w-12 h-12 rounded-full text-2xl font-bold text-red-600 bg-red-100">
                {framework?.summary?.nonCompliant || controls.filter(c => c.status === 'FAIL' || c.status === 'Non Compliant').length}
              </p>
              <p className="text-sm text-[--ds-text-900] font-medium">Non-Compliant</p>
            </div>
            <div className="text-center">
              <p className="inline-flex justify-center items-center w-12 h-12 rounded-full text-2xl font-bold text-yellow-600 bg-yellow-100">
                {framework?.summary?.partiallyCompliant || controls.filter(c => c.status === 'PARTIAL' || c.status === 'Partially Compliant').length}
              </p>
              <p className="text-sm text-[--ds-text-900] font-medium">Partially Compliant</p>
            </div>
            <div className="text-center">
              <p className="inline-flex justify-center items-center w-12 h-12 rounded-full text-2xl font-bold text-gray-600 bg-gray-100 ">{framework?.summary?.notEvaluated || 0}</p>
              <p className="text-sm text-[--ds-text-900] font-medium">Not Evaluated</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-[--main-ds-card-grad] shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] rounded-md">

        {/* Controls Header */}
        <div className="mb-4">
          <h2 className="text-xl font-bold text-[--ds-text-900]">Control Details ({filteredControls.length} controls)</h2>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search controls by name, ID, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-[--ds-secondary-bg] w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none shadow-none placeholder:text-sm"
            />
          </div>
        </div>

        {/* Controls Table */}
        <div className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-[--ds-gray-50-bg]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--ds-text-500] uppercase tracking-wider">
                    Scanner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--ds-text-500] uppercase tracking-wider">
                    Control
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--ds-text-500] uppercase tracking-wider min-w-[400px]">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--ds-text-500] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--ds-text-500] uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--ds-text-500] uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[--ds-text-500] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-[--ds-secondary-bg] ">
                {filteredControls.map((control) => (
                  <React.Fragment key={control.controlId}>
                    <tr className="hover:bg-[--ds-gray-50-bg]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {control.scanner || 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-[--ds-text-900]">{control.controlId}</div>
                          <div className="text-sm text-[--ds-text-500]">{control.controlName}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-[--ds-text-900] ">
                          {control.description}
                          {control.finding && (
                            <div className="mt-1">
                              <span className="text-xs font-medium text-[--ds-text-600]">Finding:</span>
                              <span className="text-xs text-[--ds-text-500] ml-1">{control.finding}</span>
                            </div>
                          )}
                          {control.evidence && (
                            <div className="mt-1">
                              <span className="text-xs font-medium text-[--ds-text-600]">Evidence:</span>
                              <span className="text-xs text-[--ds-text-500] ml-1">{control.evidence}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(control.status)}
                          {getStatusBadge(control.status)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPriorityBadge(control.priority)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 w-16">
                            <div
                              className={`h-2 rounded-full ${getProgressBarColor(control.score)}`}
                              style={{ width: `${control.score}%` }}
                            ></div>
                          </div>
                          <span className={`text-sm font-medium ${getScoreColor(control.score)}`}>
                            {control.score}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => toggleControlExpansion(control.controlId)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <ChevronDown
                            className={`w-4 h-4 mr-1 transform transition-transform ${expandedControls[control.controlId] ? 'rotate-180' : ''
                              }`}
                          />
                          View Details
                        </button>
                      </td>
                    </tr>
                    {expandedControls[control.controlId] && (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 bg-[--ds-gray-50-bg]">
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-sm font-medium text-[--ds-text-900] mb-2">Remediation</h4>
                                <p className="text-sm text-[--ds-text-600]">
                                  {control.actions?.remediation || 'Implement Content-Security-Policy header with appropriate directives to prevent XSS and data injection attacks'}
                                </p>
                                <div className="mt-2">
                                  <span className="text-xs font-medium text-[--ds-text-600]">Timeline:</span>
                                  <span className="text-xs text-[--ds-text-500] ml-1">
                                    {control.actions?.timeline || 'Immediate (0-7 days)'}
                                  </span>
                                </div>
                                <div className="mt-1">
                                  <span className="text-xs font-medium text-[--ds-text-600]">CWE:</span>
                                  <span className="text-xs text-[--ds-text-500] ml-1">
                                    {control.actions?.cwe || 'CWE-693'}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-[--ds-text-900] mb-2">References</h4>
                                <ul className="text-sm text-[--ds-text-600] space-y-1 p-0 m-0">
                                  {control.actions?.references?.map((ref, index) => (
                                    <li key={index} className="flex items-center">
                                      <ExternalLink className="w-3 h-3 mr-2 text-blue-600" />
                                      {ref}
                                    </li>
                                  )) || (
                                      <>
                                        <li className="flex items-center">
                                          <ExternalLink className="w-3 h-3 mr-2 text-blue-600" />
                                          {frameworkId === 'USWDS' ? 'USWDS Compliance Documentation' : 'OMB M-15-13 HTTPS Everywhere Documentation'}
                                        </li>
                                        <li className="flex items-center">
                                          <ExternalLink className="w-3 h-3 mr-2 text-blue-600" />
                                          OWASP {frameworkId === 'USWDS' ? 'Design System' : 'HTTPS'} Prevention
                                        </li>
                                      </>
                                    )}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FrameworkDetails;
